/** Extra customer fields, defined by an admin (§4.2.4). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface CustomField {
  id: string;
  key: string;
  label: string;
  type: 'TEXT' | 'NUMBER' | 'DATE' | 'DROPDOWN';
  required: boolean;
  active: boolean;
  parameterGroupId: string | null;
  parameterGroup?: { label: string } | null;
}

interface Group {
  id: string;
  key: string;
  label: string;
}

const TYPE_LABELS: Record<CustomField['type'], string> = {
  TEXT: 'Text',
  NUMBER: 'Number',
  DATE: 'Date',
  DROPDOWN: 'Dropdown',
};

export function CustomFieldsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    key: '',
    label: '',
    type: 'TEXT' as CustomField['type'],
    parameterGroupId: '',
    required: false,
  });
  const [error, setError] = useState<string | null>(null);

  const fields = useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => api<{ data: CustomField[] }>('/parameters/custom-fields'),
  });
  const groups = useQuery({
    queryKey: ['parameters', 'groups'],
    queryFn: () => api<{ data: Group[] }>('/parameters'),
  });

  const create = useMutation({
    mutationFn: () =>
      api('/parameters/custom-fields', {
        method: 'POST',
        body: {
          key: form.key.trim(),
          label: form.label.trim(),
          type: form.type,
          required: form.required,
          ...(form.type === 'DROPDOWN' ? { parameterGroupId: form.parameterGroupId } : {}),
        },
      }),
    onSuccess: () => {
      setError(null);
      setAdding(false);
      setForm({ key: '', label: '', type: 'TEXT', parameterGroupId: '', required: false });
      void queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not add the field.'),
  });

  const toggle = useMutation({
    mutationFn: (field: CustomField) =>
      api(`/parameters/custom-fields/${field.id}`, {
        method: 'PATCH',
        body: { active: !field.active },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['custom-fields'] }),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Customer fields</h1>
          <p className="text-sm text-slate-500">
            Extra fields on the customer form, without a code change. They appear in the order you
            add them.
          </p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus aria-hidden="true" className="h-4 w-4" /> Add field
        </Button>
      </header>

      {error && <Alert tone="critical">{error}</Alert>}

      <Card>
        {fields.isLoading && <Skeleton className="h-32 w-full" />}
        {fields.data?.data.length === 0 && (
          <EmptyState
            title="No extra fields yet"
            description="The standard customer form already covers name, TIN, address, category and industry."
          />
        )}
        {(fields.data?.data.length ?? 0) > 0 && (
          <TableWrap>
            <thead>
              <tr>
                <Th>Label</Th>
                <Th>Type</Th>
                <Th>Required</Th>
                <Th align="right">Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {fields.data?.data.map((field) => (
                <tr key={field.id}>
                  <Td>
                    <p className="font-medium">{field.label}</p>
                    <p className="font-mono text-xs text-slate-400">{field.key}</p>
                  </Td>
                  <Td>
                    {TYPE_LABELS[field.type]}
                    {field.parameterGroup && (
                      <span className="text-xs text-slate-500"> · {field.parameterGroup.label}</span>
                    )}
                  </Td>
                  <Td>{field.required ? 'Yes' : 'No'}</Td>
                  <Td align="right">
                    <button onClick={() => toggle.mutate(field)} title="Click to change">
                      <StatusChip tone={field.active ? 'ok' : 'neutral'}>
                        {field.active ? 'Active' : 'Hidden'}
                      </StatusChip>
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      {adding && (
        <Modal title="Add a customer field" onClose={() => setAdding(false)}>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Label" hint="What people see, e.g. “Delivery instructions”." required>
              <input
                className={inputClass}
                required
                value={form.label}
                onChange={(event) =>
                  setForm({
                    ...form,
                    label: event.target.value,
                    key: form.key || event.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                  })
                }
              />
            </Field>
            <Field label="Key" hint="Stored name; letters, numbers and underscores." required>
              <input
                className={inputClass}
                required
                value={form.key}
                onChange={(event) => setForm({ ...form, key: event.target.value })}
              />
            </Field>
            <Field label="Type" required>
              <select
                className={inputClass}
                value={form.type}
                onChange={(event) =>
                  setForm({ ...form, type: event.target.value as CustomField['type'] })
                }
              >
                {Object.entries(TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            {form.type === 'DROPDOWN' && (
              <Field label="Choices from" hint="Which parameter list fills the dropdown." required>
                <select
                  className={inputClass}
                  required
                  value={form.parameterGroupId}
                  onChange={(event) => setForm({ ...form, parameterGroupId: event.target.value })}
                >
                  <option value="">Choose a list</option>
                  {groups.data?.data.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                checked={form.required}
                onChange={(event) => setForm({ ...form, required: event.target.checked })}
              />
              Required on the customer form
            </label>
            <div className="flex gap-2">
              <Button type="submit" busy={create.isPending}>
                Add field
              </Button>
              <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
